/**
 * Admin CRUD schemas for the opt-out keywords registry.
 * Source of truth: docs/architecture/database-schema.md §4.10 + Story 3.9.
 *
 * **Match types:**
 *   - `literal`  → case-insensitive substring match (DB column `is_regex = FALSE`)
 *   - `regex`    → JS RegExp match (DB column `is_regex = TRUE`)
 *
 * Validation:
 *   - keyword min 2 / max 200 chars
 *   - regex: API verifies `new RegExp(keyword)` is constructible (route-level)
 *
 * **Constitutional Article IV (No Invention):** every field traces to §4.10
 * (`opt_out_keywords` table) — `keyword`, `is_regex`, `active`, `created_by`,
 * `created_at`, `updated_at`. The wire enum `match_type` is a UX-friendly
 * surface over the existing `is_regex` boolean (no new column).
 */
import { Type } from '@sinclair/typebox';
/**
 * Wire-side match type. Mapped to DB `is_regex` boolean by the route handler.
 */
export const OptOutMatchType = Type.Union([Type.Literal('literal'), Type.Literal('regex')], { $id: 'OptOutMatchType' });
export const OPT_OUT_MATCH_TYPES = ['literal', 'regex'];
/**
 * Keyword length bounds — chosen to avoid catastrophic regex storage and
 * still allow long literal phrases (e.g. customer-pasted opt-out sentences).
 */
export const OPT_OUT_KEYWORD_MIN = 2;
export const OPT_OUT_KEYWORD_MAX = 200;
/**
 * `OptOutKeyword` — full read-side projection returned by GET endpoints.
 */
export const OptOutKeyword = Type.Object({
    id: Type.String({ format: 'uuid' }),
    keyword: Type.String({ minLength: OPT_OUT_KEYWORD_MIN, maxLength: OPT_OUT_KEYWORD_MAX }),
    matchType: OptOutMatchType,
    active: Type.Boolean(),
    createdBy: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
});
/**
 * `OptOutKeywordCreate` — payload for `POST /admin/opt-out-keywords` (admin/manager).
 * `active` defaults to `true` server-side.
 */
export const OptOutKeywordCreate = Type.Object({
    keyword: Type.String({ minLength: OPT_OUT_KEYWORD_MIN, maxLength: OPT_OUT_KEYWORD_MAX }),
    matchType: OptOutMatchType,
});
/**
 * `OptOutKeywordUpdate` — payload for `PATCH /admin/opt-out-keywords/:id`.
 * All fields optional — covers active toggle (`{ active: false }`) and
 * keyword/matchType edits in a single endpoint.
 */
export const OptOutKeywordUpdate = Type.Object({
    keyword: Type.Optional(Type.String({ minLength: OPT_OUT_KEYWORD_MIN, maxLength: OPT_OUT_KEYWORD_MAX })),
    matchType: Type.Optional(OptOutMatchType),
    active: Type.Optional(Type.Boolean()),
});
/**
 * `OptOutKeywordListQuery` — query string of GET /admin/opt-out-keywords.
 * `active` accepts a string ("true"|"false") because TypeBox + Fastify
 * query coercion of booleans is unreliable across versions.
 */
export const OptOutKeywordListQuery = Type.Object({
    matchType: Type.Optional(OptOutMatchType),
    active: Type.Optional(Type.String()),
    search: Type.Optional(Type.String()),
});
export const OptOutKeywordListResponse = Type.Object({
    keywords: Type.Array(OptOutKeyword),
});
export const OptOutKeywordResponse = Type.Object({
    keyword: OptOutKeyword,
});
export const OptOutKeywordInvalidateResponse = Type.Object({
    invalidated: Type.Boolean(),
});
//# sourceMappingURL=opt-out-keywords.js.map